import { useState, type ReactNode } from 'react'
import { Check, ChevronRight, Loader2, Minimize2, ShieldQuestion, X } from 'lucide-react'
import type { Block, Timeline } from '@one-deployment/claude-stream'
import type { ChatQuestion } from '@one-deployment/protocol'
import { Conversation, ConversationContent, ConversationScrollButton } from '@/components/ai-elements/conversation'
import { Message, MessageContent, MessageResponse } from '@/components/ai-elements/message'
import { Button } from '@/components/shadcn/button'
import { Card } from '@/components/shadcn/card'
import { cn } from '@/lib/cn'
import { describe, Thinking, UserMessage, type MessageActions } from './Transcript'
import { LimitNotice, isLimitText } from './LimitNotice'
import { footer, type Answer } from './stream'

type Perm = Extract<Block, { kind: 'permission' }>
type Tool = Extract<Block, { kind: 'tool' }>

/**
 * A headless session drawn from Claude Code's own stream: text as it is
 * written, every tool with its result and a subagent's work nested under it,
 * permission prompts as cards anyone with the session can answer.
 */
export function StreamTranscript({ tl, working, readOnly, onAnswer, question, sent = [], held = [], actions = {}, live }: {
  tl: Timeline | null; working: boolean; readOnly: boolean
  onAnswer: (p: Perm, a: Answer) => void
  /** How an AskUserQuestion prompt is asked: the pane's question form. */
  question: (qs: ChatQuestion[], answer: (answers: Record<string, string>) => void) => ReactNode
  sent?: { id: string; text: string }[]
  /** Written while Claude works and kept here: Cancel, or Send now; the first goes in when the turn ends. */
  held?: { id: string; text: string }[]
  actions?: MessageActions
  /** The live status lines (ONE-9), under the working line. */
  live?: ReactNode
}) {
  const blocks = tl?.blocks ?? []
  return (
    <Conversation className="min-h-0" initial="instant" resize="instant">
      <ConversationContent className="gap-4 px-6 py-5">
        {!tl && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 size={14} className="animate-spin" />Loading the conversation…</div>}
        {tl && !blocks.length && <div className="py-10 text-center text-sm text-fg-faint">Nothing said yet.</div>}
        {blocks.map((b) => b.kind === 'user'
          ? <UserMessage key={b.id} text={userText(b)} rewind={actions.rewindStream && b.prev !== undefined ? () => actions.rewindStream!(b) : undefined} />
          : <BlockView key={b.id} b={b} readOnly={readOnly} onAnswer={onAnswer} question={question} />)}
        {sent.map((q) => <UserMessage key={q.id} text={q.text} note="Sent" />)}
        {tl && (working || blocks.length > 0) && <div className="flex items-center gap-2 text-[11px] text-fg-faint">
          {working && <Loader2 size={12} className="animate-spin" />}
          <span>{working ? activityLabel(tl) : 'Ready'}</span>
          <span className="ml-auto">{footer(tl)}</span>
        </div>}
        {live}
        {/* Below the live line: not sent yet. */}
        {held.map((q) => <UserMessage key={q.id} text={q.text} held cancel={() => actions.cancel?.(q.id)} sendNow={() => actions.sendNow?.(q.id)} />)}
      </ConversationContent>
      <ConversationScrollButton className="bottom-3 border bg-surface-3" />
    </Conversation>
  )
}

const userText = (b: Extract<Block, { kind: 'user' }>) => b.text + (b.images ? `\n\n(${b.images} image${b.images > 1 ? 's' : ''})` : '')

const activityLabel = (t: Timeline) => ({
  requesting: 'Asking Claude…', thinking: 'Thinking…', writing: 'Writing…', tool: 'Running a tool…',
  compacting: 'Compacting the conversation…', waiting: 'Waiting for an answer', idle: '', exited: '',
} as const)[t.activity]

function BlockView({ b, readOnly, onAnswer, question }: {
  b: Block; readOnly: boolean; onAnswer: (p: Perm, a: Answer) => void
  question: (qs: ChatQuestion[], answer: (answers: Record<string, string>) => void) => ReactNode
}) {
  switch (b.kind) {
    case 'user': return <UserMessage text={userText(b)} />
    case 'text': if (isLimitText(b.text)) return <LimitNotice text={b.text} />
      return <Message from="assistant" className="max-w-none"><MessageContent className="w-full"><MessageResponse>{b.text}</MessageResponse></MessageContent></Message>
    case 'thinking': return b.redacted ? null : <Thinking text={b.text} />
    case 'tool': return <ToolCard t={b} readOnly={readOnly} onAnswer={onAnswer} question={question} />
    case 'permission': return <PermissionCard p={b} readOnly={readOnly} onAnswer={onAnswer} question={question} />
    case 'compact': return <Divider>Conversation compacted{b.preTokens ? ` from ${Math.round(b.preTokens / 1000)}k tokens` : ''}</Divider>
    case 'notice': return b.level === 'info' && b.title === 'Started' ? null : (
      <div className={cn('rounded-lg border px-3 py-2 text-xs', b.level === 'error' ? 'border-err/40 bg-err/10' : b.level === 'warn' ? 'border-warn/40 bg-warn/10' : 'border-border bg-surface-2')}>
        <span className="font-medium">{b.title}</span>{b.text && <span className="ml-2 whitespace-pre-wrap text-fg-muted">{b.text}</span>}
      </div>
    )
    case 'result': return b.ok ? null : <Divider tone="err">{b.subtype === 'error_during_execution' ? 'Stopped' : `Turn ended: ${b.errors[0] ?? b.subtype}`}</Divider>
    case 'unknown': return <div className="font-mono text-[11px] text-fg-faint">{b.label}</div>
  }
}

function Divider({ children, tone }: { children: ReactNode; tone?: 'err' }) {
  return <div className={cn('flex items-center gap-2 text-[11px]', tone === 'err' ? 'text-err' : 'text-fg-faint')}><Minimize2 size={11} />{children}<span className="h-px flex-1 bg-border" /></div>
}

const STATUS: Record<Tool['status'], ReactNode> = {
  streaming: <Loader2 size={12} className="animate-spin text-fg-faint" />, running: <Loader2 size={12} className="animate-spin text-accent" />,
  done: <Check size={12} className="text-ok" />, error: <X size={12} className="text-err" />,
  denied: <X size={12} className="text-warn" />, interrupted: <X size={12} className="text-fg-faint" />,
}

function ToolCard({ t, readOnly, onAnswer, question }: {
  t: Tool; readOnly: boolean; onAnswer: (p: Perm, a: Answer) => void
  question: (qs: ChatQuestion[], answer: (answers: Record<string, string>) => void) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const d = describe(t.name, t.input)
  return (
    <div className="rounded-lg border border-border bg-surface-2 text-xs" data-tool={t.name} data-status={t.status}>
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left">
        <ChevronRight size={12} className={cn('shrink-0 transition-transform', open && 'rotate-90')} />
        <span className="shrink-0 text-fg-subtle">{d.icon}</span>
        <span className="shrink-0 font-medium">{d.title}</span>
        {d.detail && <span className="min-w-0 truncate font-mono text-fg-faint">{d.detail}</span>}
        <span className="ml-auto shrink-0">{STATUS[t.status]}</span>
      </button>
      {open && <div className="flex flex-col gap-2 border-t border-border px-3 py-2">
        <pre className="max-h-60 overflow-auto whitespace-pre-wrap font-mono text-[11px] text-fg-muted">{t.inputText || JSON.stringify(t.input, null, 2)}</pre>
        {t.result && <pre className={cn('max-h-80 overflow-auto whitespace-pre-wrap font-mono text-[11px]', t.result.isError ? 'text-err' : 'text-fg-muted')}>
          {t.result.text || (t.result.images ? `(${t.result.images} image)` : '(no output)')}
        </pre>}
      </div>}
      {/* A subagent's own work, under the call that started it. */}
      {t.children.length > 0 && <div className="flex flex-col gap-2 border-t border-border py-2 pl-5 pr-2">
        {t.children.map((c) => <BlockView key={c.id} b={c} readOnly={readOnly} onAnswer={onAnswer} question={question} />)}
      </div>}
    </div>
  )
}

function PermissionCard({ p, readOnly, onAnswer, question }: {
  p: Perm; readOnly: boolean; onAnswer: (p: Perm, a: Answer) => void
  question: (qs: ChatQuestion[], answer: (answers: Record<string, string>) => void) => ReactNode
}) {
  // A question Claude asks is answered by allowing the tool with the answers on its input.
  if (p.tool === 'AskUserQuestion' && !p.answer && Array.isArray(p.input.questions)) {
    return <>{question(p.input.questions as ChatQuestion[], (answers) => onAnswer(p, { allow: true, input: { ...p.input, answers } }))}</>
  }
  const d = describe(p.tool, p.input)
  const title = p.tool === 'ExitPlanMode' ? 'Claude has a plan and asks to start' : `Claude wants to use ${p.tool}`
  return (
    <Card className="gap-0 overflow-hidden rounded-lg py-0" data-permission={p.id}>
      <div className="flex items-center gap-2 border-b border-border bg-accent/5 px-3 py-2 text-xs font-medium text-accent"><ShieldQuestion size={14} />{title}</div>
      <div className="flex flex-col gap-1 px-3 py-2 text-xs">
        {p.description && <span>{p.description}</span>}
        {p.tool === 'ExitPlanMode' && typeof p.input.plan === 'string'
          ? <MessageResponse>{p.input.plan}</MessageResponse>
          : <span className="font-mono text-fg-muted">{d.detail ?? JSON.stringify(p.input)}</span>}
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-border px-3 py-2">
        {p.answer
          ? <span className="text-xs text-fg-subtle">{p.answer === 'allow' ? 'Allowed' : p.answer === 'deny' ? 'Denied' : 'Cancelled'}</span>
          : readOnly ? <span className="text-xs text-fg-faint">Waiting for someone who can answer</span>
          : <>
              <Button variant="outline" size="sm" onClick={() => onAnswer(p, { allow: false })}>Deny</Button>
              <Button size="sm" onClick={() => onAnswer(p, { allow: true })}>Allow</Button>
            </>}
      </div>
    </Card>
  )
}
